import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { applyConsent, canBuildDurableProfile, readConsent } from './consent';
import {
  hubspotIdentify,
  isHubSpotEnabled,
  isHubSpotLoaded,
  setHubSpotPixelAllowed
} from './hubspot';

import { fetchHubSpotIdentity } from '@/shared/api/hubspotIdentity';
import { fetchCookieBanner } from '@/shared/api/legal';
import { useAuth } from '@/shared/hooks/useAuth';

/**
 * Owns the HubSpot pixel's lifecycle: whether it may load, and whom it is
 * allowed to associate the browser with.
 *
 * Kept separate from AnalyticsBootstrap on purpose. Both are analytics
 * bootstrappers, but this one needs react-query (two server answers gate it)
 * whereas AnalyticsBootstrap is deliberately provider-free and synchronous —
 * merging them would drag a QueryClient dependency into the PostHog identity
 * path, which is a wider blast radius than this feature deserves.
 *
 * Mounted in App inside QueryProvider.
 */
const HubSpotGate: React.FC = () => {
  const user = useAuth((s) => s.user);
  const lastIdentifiedUserId = useRef<string | null>(null);

  // ── Gate 1: may the pixel load in this environment at all? ────────────────
  //
  // Shares CookieBanner's query key, so this is served from cache rather than
  // costing a second request.
  const { data: banner } = useQuery({
    queryKey: ['legal', 'cookieBanner'],
    queryFn: fetchCookieBanner,
    staleTime: 1000 * 60 * 10,
    retry: 1,
    enabled: isHubSpotEnabled
  });

  useEffect(() => {
    if (!isHubSpotEnabled || banner === undefined) return;

    setHubSpotPixelAllowed(banner.hubspotPixelEnabled);

    // Re-run the visitor's stored decision now that the gate's answer is known.
    // Boot already applied it once with the gate shut, so this is the step that
    // actually injects the pixel for a consenting visitor — and equally the step
    // that tears it down if the flag was switched off since the last page load.
    const stored = readConsent();
    if (stored) applyConsent(stored.categories);
  }, [banner]);

  // ── Gate 2: is this user already a HubSpot contact? ───────────────────────
  //
  // Only asked once the pixel is live and the visitor allowed a durable profile,
  // so a rejecting visitor costs no request.
  const canAssociate =
    Boolean(user) && isHubSpotLoaded() && canBuildDurableProfile();

  const { data: identity } = useQuery({
    queryKey: ['analytics', 'hubspotIdentity', user?.id ?? null],
    queryFn: fetchHubSpotIdentity,
    enabled: canAssociate,
    staleTime: 1000 * 60 * 60,
    retry: 1
  });

  useEffect(() => {
    if (!canAssociate || !user?.email) {
      lastIdentifiedUserId.current = null;
      return;
    }

    // THE COST CONTROL. `identify` + pageview is an upsert: HubSpot offers no
    // "associate only if the contact exists" call, so this check is the only
    // thing standing between a portal login and a newly created — and
    // potentially billable — CRM contact. Removing it would silently turn every
    // signed-in user into a contact and push the account toward its
    // marketing-contact ceiling, which cannot be undone.
    //
    // Guarded by a test in __tests__/HubSpotGate.test.tsx.
    if (identity?.known !== true) return;

    // `hubspotIdentify` flushes with a pageview, so an unguarded re-run would
    // inflate HubSpot's pageview count on every render touching this effect.
    if (lastIdentifiedUserId.current === user.id) return;

    hubspotIdentify(user.email);
    lastIdentifiedUserId.current = user.id;
  }, [canAssociate, user?.email, user?.id, identity?.known]);

  return null;
};

export default HubSpotGate;
